import { useEffect, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from 'react'
import SidePanelResizeHandle from '@features/shared/components/SidePanelResizeHandle'
import { useSidebarMenuWidthStore } from '../stores/useSidebarMenuWidthStore'

function cssPixels(element: Element, property: string, fallback: number) {
  return Number.parseFloat(getComputedStyle(element).getPropertyValue(property)) || fallback
}

/**
 * The pinned menu card's right edge, dragged to make the menu wider or
 * narrower (between --sidebar-drawer-min-width and --sidebar-drawer-max-width),
 * so long names fit. Down the middle of the gap beside the card, like a page's
 * side panel's handle.
 */
export function SidebarMenuResizeHandle({ cardRef }: { cardRef: RefObject<HTMLElement | null> }) {
  const isResizing = useSidebarMenuWidthStore((state) => state.isResizing)
  const resizeCleanup = useRef<(() => void) | null>(null)

  useEffect(() => () => resizeCleanup.current?.(), [])

  const startResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    const card = cardRef.current
    if (event.button !== 0 || !card) return
    event.preventDefault()
    resizeCleanup.current?.()
    const { setWidth, setResizing } = useSidebarMenuWidthStore.getState()
    const startWidth = card.getBoundingClientRect().width
    const minWidth = cssPixels(card, '--sidebar-drawer-min-width', 240)
    const maxWidth = cssPixels(card, '--sidebar-drawer-max-width', 560)
    const startX = event.clientX
    const previousCursor = document.body.style.cursor
    const previousUserSelect = document.body.style.userSelect
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    setResizing(true)
    const move = (next: PointerEvent) =>
      setWidth(
        Math.round(Math.max(minWidth, Math.min(maxWidth, startWidth + next.clientX - startX)))
      )
    const cleanup = () => {
      document.body.style.cursor = previousCursor
      document.body.style.userSelect = previousUserSelect
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', cleanup)
      window.removeEventListener('pointercancel', cleanup)
      resizeCleanup.current = null
      setResizing(false)
    }
    resizeCleanup.current = cleanup
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', cleanup)
    window.addEventListener('pointercancel', cleanup)
  }

  return (
    <SidePanelResizeHandle
      edge="end"
      label="Resize menu"
      isResizing={isResizing}
      onPointerDown={startResize}
    />
  )
}
